import { put, take, call, select } from 'redux-saga/effects';
import debug from 'debug';
import { omit } from 'lodash';
import {
  initialize as initializeForm,
  change as changeFormField,
} from 'redux-form';

import {
  AD_CREATED,
  STORE_AD,
  RESTORE_AD,
  CLEAR_SAVED_AD,
  LOAD_AD,
  loadAd,
  clearSavedAd,
  restoreAd,
} from 'actions/editable-ad';
import { validateFields } from 'actions/validation';
import { getCombinedAd } from 'selectors/adCreation';
import { getReduxFormValues } from 'selectors/reduxForm';
import {
  getStorage,
  setStorage,
  removeStorage,
  jsonParse,
  jsonStringify,
  isKeyExpired,
  getTimeout,
} from 'utils/storageHelpers';

const log = debug('sagas:adStorage');

export const SHOULD_RESTORE_FLAG = 'should-restore-ad-state';
export const AD_STORAGE_KEY = 'adgen-ad-state';
export const REDUX_FORM_STORAGE_KEY = 'adgen-redux-form-state';

// TODO kill this method once redux-form is 100%
export function* saveEditableAd(storage) {
  log('saving the ad');
  const data = yield select(getCombinedAd);
  const stringifiedData = jsonStringify(omit(data, 'activeStep'));
  yield call(setStorage, storage, AD_STORAGE_KEY, stringifiedData);
  log('saved the ad');
}

// TODO kill this method once redux-form is 100%
export function* restoreEditableAd(storage) {
  log('restoring ad data into editableAd');

  const adToRestore = yield call(getStorage, storage, AD_STORAGE_KEY);

  if (!adToRestore) {
    log('no ad found to restore');
    return;
  }

  yield put(loadAd(jsonParse(adToRestore)));
}

export function* saveReduxForms(storage) {
  const data = yield select(getReduxFormValues);
  const stringifiedData = jsonStringify(data);
  yield call(setStorage, storage, REDUX_FORM_STORAGE_KEY, stringifiedData);
  log('saved the redux forms');
}

export function* restoreReduxForms(storage) {
  log('restoring ad data into redux-forms');

  const formsToRestore = yield call(
    getStorage,
    storage,
    REDUX_FORM_STORAGE_KEY
  );
  if (!formsToRestore) {
    log('no redux forms found to restore');
    return;
  }

  // yeilding this is unnecessary, but makes testing much simpler.
  const reduxFormsObj = yield call(jsonParse, formsToRestore);

  // NOTE: these need to be blocks and not _.map()s of yields because we don't
  // have access to redux-saga's all() function with our current version.
  // for each form, reset its initialValues object and trigger a reset.
  for (const formName in reduxFormsObj) {
    if (Object.prototype.hasOwnProperty.call(reduxFormsObj, formName)) {
      // initialize the form first so that the form doesn't reinitialize
      // if the user hits back, which would clobber the values.
      yield put(initializeForm(formName, {}));

      // iterate through the values and set them as changes.
      const values = reduxFormsObj[formName];
      for (const fieldName in values) {
        if (Object.prototype.hasOwnProperty.call(values, fieldName)) {
          const value = values[fieldName];
          yield put(changeFormField(formName, fieldName, value));
        }
      }
    }
  }
}

export function* watchAdStored(storage = window.localStorage) {
  yield take(STORE_AD);

  log('detected a request to save an ad');

  yield saveEditableAd(storage); // TODO kill this call once redux-form is 100%
  yield saveReduxForms(storage);

  const restoreJson = yield call(getTimeout);
  yield call(setStorage, storage, SHOULD_RESTORE_FLAG, restoreJson);
}

export function* watchAdRestore(storage = window.localStorage) {
  yield take(RESTORE_AD);

  log('detected a request to restore an ad');

  yield restoreEditableAd(storage); // TODO kill this call once redux-form is 100%
  yield restoreReduxForms(storage);
}

export function* watchClearSavedAd(storage = window.localStorage) {
  yield take(CLEAR_SAVED_AD);

  log('detected a request to clear a saved ad');

  yield call(removeStorage, storage, AD_STORAGE_KEY); // TODO kill this call once redux-form is 100%
  yield call(removeStorage, storage, REDUX_FORM_STORAGE_KEY);
  yield call(removeStorage, storage, SHOULD_RESTORE_FLAG);
}

export function* watchAdCreated() {
  yield take(AD_CREATED);

  log('detected that an ad was created');

  yield put(clearSavedAd());
}

// TODO clear this watcher and remove the listener in the main saga file
// when redux-form is 100%
export function* watchLoadAd() {
  yield take(LOAD_AD);

  log('validating the loaded ad');

  const fields = yield select(getCombinedAd);
  yield put(validateFields({ fields }));
}

export function* checkForAdRestoreFlag(storage = window.localStorage) {
  const restoreJSON = yield call(getStorage, storage, SHOULD_RESTORE_FLAG);
  if (restoreJSON) {
    const hasExpired = yield call(isKeyExpired, storage, SHOULD_RESTORE_FLAG);

    if (!hasExpired) {
      yield put(restoreAd());
    } else {
      yield put(clearSavedAd());
    }
  }
}



// WEBPACK FOOTER //
// ./src/middleware/sagas/adStorage.js